// src/components/admin/EditSettingModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, RotateCcw, Save, AlertTriangle } from 'lucide-react';
import { TenantConfiguration, tenantConfigAPI } from '../../services/tenantConfigAPI';

interface EditSettingModalProps {
  isOpen: boolean;
  setting: TenantConfiguration;
  onClose: () => void;
  onSave: (updatedSetting: TenantConfiguration) => void;
}

const EditSettingModal: React.FC<EditSettingModalProps> = ({
  isOpen,
  setting,
  onClose,
  onSave
}) => {
  const [values, setValues] = useState<string[]>([]);
  const [newValue, setNewValue] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with setting data
  useEffect(() => {
    if (setting) {
      setValues([...setting.values]);
      setDisplayName(setting.displayName);
      setDescription(setting.description || '');
      setNewValue('');
      setError(null);
      setHasChanges(false);
    }
  }, [setting]);

  // Track changes
  useEffect(() => {
    const valuesChanged = JSON.stringify(values) !== JSON.stringify(setting.values);
    const nameChanged = displayName !== setting.displayName;
    const descChanged = description !== (setting.description || '');
    
    setHasChanges(valuesChanged || nameChanged || descChanged);
  }, [values, displayName, description, setting]);

  const handleAddValue = () => {
    const trimmedValue = newValue.trim();
    
    if (!trimmedValue) {
      setError('Value cannot be empty');
      return;
    }
    
    if (values.some(v => v.toLowerCase() === trimmedValue.toLowerCase())) {
      setError('This value already exists');
      return;
    }
    
    setValues(prev => [...prev, trimmedValue]);
    setNewValue('');
    setError(null);
  };

  const handleRemoveValue = (index: number) => {
    setValues(prev => prev.filter((_, i) => i !== index));
  };

  const handleMoveValue = (index: number, direction: 'up' | 'down') => {
    const newValues = [...values];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newValues.length) {
      [newValues[index], newValues[targetIndex]] = [newValues[targetIndex], newValues[index]];
      setValues(newValues);
    }
  };

  const handleResetToDefault = async () => {
    if (!window.confirm('Reset this setting to system defaults? All custom changes will be lost.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await tenantConfigAPI.resetToDefault(setting.configurationKey);
      
      // @ts-ignore - API returns nested data structure
      if (response.success && response.data?.data?.configuration) {
        // @ts-ignore - API returns nested data structure
        const resetSetting = response.data.data.configuration;
        setValues([...resetSetting.values]);
        setDisplayName(resetSetting.displayName);
        setDescription(resetSetting.description || '');
        setError(null);
      } else {
        setError(response.error?.message || 'Failed to reset to default');
      }
    } catch (err) {
      setError('Failed to reset to default');
      console.error('Reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (values.length === 0) {
      setError('At least one value is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await tenantConfigAPI.updateConfiguration(setting.configurationKey, {
        displayName,
        description,
        values
      });

      // @ts-ignore - API returns nested data structure
      if (response.success && response.data?.data?.configuration) {
        // @ts-ignore - API returns nested data structure
        onSave(response.data.data.configuration);
        onClose();
      } else {
        setError(response.error?.message || 'Failed to save changes');
      }
    } catch (err) {
      setError('Failed to save changes');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddValue();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Edit {setting.displayName}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure {setting.description.toLowerCase()}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertTriangle className="w-5 h-5 text-red-400 mr-3 mt-0.5" />
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
          </div>

          {/* Current Values */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Current Values ({values.length})
            </label>
            
            <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
              {values.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No values configured
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {values.map((value, index) => (
                    <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-gray-600">#{index + 1}</span>
                        <span className="text-sm text-gray-900">{value}</span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        {/* Move buttons */}
                        {index > 0 && (
                          <button
                            onClick={() => handleMoveValue(index, 'up')}
                            disabled={loading}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            title="Move up"
                          >
                            ↑
                          </button>
                        )}
                        {index < values.length - 1 && (
                          <button
                            onClick={() => handleMoveValue(index, 'down')}
                            disabled={loading}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            title="Move down"
                          >
                            ↓
                          </button>
                        )}
                        
                        {/* Delete button */}
                        <button
                          onClick={() => handleRemoveValue(index)}
                          disabled={loading}
                          className="p-1 text-red-400 hover:text-red-600 disabled:opacity-50"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add New Value */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add New Value
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new value..."
                disabled={loading}
              />
              <button
                onClick={handleAddValue}
                disabled={loading || !newValue.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </button>
            </div>
          </div>

          {/* System Default Info */}
          {setting.isSystemDefault && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-start">
                <div className="text-blue-400 mr-3 mt-0.5">ℹ️</div>
                <div className="text-sm text-blue-700">
                  <strong>System Default:</strong> This is a system-provided configuration. 
                  You can modify the values, but they will be preserved during system updates.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <div className="flex space-x-3">
            {setting.isSystemDefault && (
              <button
                onClick={handleResetToDefault}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Default
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !hasChanges || values.length === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSettingModal;
